// Runtime settings, grouped by what they control. Edits collect in a draft; saving shows every
// change side by side first, and the ones that move money or switch Claude off are called out.
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowRight,
  CircleAlert,
  ExternalLink,
  Minus,
  Monitor,
  Plus,
  RotateCcw,
  Smartphone,
  TriangleAlert,
} from 'lucide-react';
import {
  bunnyVideo,
  GUIDE_VIDEO_KINDS,
  type GuideVideoKind,
  type GuideVideos,
} from '@gaintplay/core/guide';
import { LOCALE_INFO, LOCALES } from '@gaintplay/core/i18n';
import { api } from '../lib/api';
import { useCan } from '../lib/staff';
import {
  actorLabel,
  confirmAction,
  ErrorNote,
  fmtNum,
  fmtUsd,
  PageHeader,
  TimeAgo,
  toast,
} from '../components/ui';

type Value = boolean | number | string | number[] | GuideVideos;
interface SettingsData {
  settings: Record<string, Value>;
  defaults: Record<string, Value>;
  changed: { key: string; updatedBy: string; updatedAt: number }[];
  staffNames: Record<string, string>;
}

const COINS_PER_USD = 1000;
const MODELS = [
  ['claude-opus-5-5', 'Claude Opus 5.5 (most careful)'],
  ['claude-sonnet-5-5', 'Claude Sonnet 5.5 (balanced)'],
  ['claude-haiku-5-5', 'Claude Haiku 5.5 (fastest, cheapest)'],
] as const;

type Kind =
  | { type: 'toggle'; on: string; off: string }
  | { type: 'mode' }
  | { type: 'coins'; min: number; max: number; zero?: string }
  /** A plain whole number with a unit ("days", "times"); `zero` names what 0 means. */
  | { type: 'count'; min: number; max: number; unit: string; zero?: string }
  | { type: 'usd'; min: number; max: number; zero?: string }
  | { type: 'percent'; min: number; max: number }
  | { type: 'bps'; max: number }
  | { type: 'model' }
  | { type: 'days' }
  | { type: 'datetime' }
  | { type: 'text'; max: number; placeholder?: string; rows?: number }
  /** One https link on one of `hosts`, or empty (same rule as the server's). */
  | { type: 'link'; placeholder: string; hosts: string[] }
  /** A phone and a desktop video link for every language (GuideVideosEditor). */
  | { type: 'guideVideos' };

interface Field {
  key: string;
  label: string;
  help: string;
  kind: Kind;
  /** Why this change deserves a second look, shown in the save dialog. */
  danger?: (from: Value, to: Value) => string | null;
  /** The control takes the row's full width, under the label (a table rather than one box). */
  wide?: boolean;
}

const GROUPS: { title: string; blurb: string; fields: Field[] }[] = [
  {
    title: 'Cash-outs',
    blurb: 'Who can cash out, and when sending stops on its own.',
    fields: [
      {
        key: 'payouts.enabled',
        label: 'Cash-outs open',
        help: 'Off stops new requests and stops sending approved ones. Nothing already sent is affected.',
        kind: { type: 'toggle', on: 'Open', off: 'Paused' },
        danger: (_, to) =>
          to === false ? 'Users will see cash-outs as paused until you turn this back on.' : null,
      },
      {
        key: 'payouts.firstCashoutCoins',
        label: 'Balance needed for a first cash-out',
        help: 'A user must have at least this many available coins before their first cash-out.',
        kind: { type: 'coins', min: 0, max: 1_000_000, zero: 'No minimum' },
      },
      {
        key: 'payouts.minCoins',
        label: 'Smallest cash-out after that',
        help: 'Applies from the second cash-out on. 0 means the card price is the only minimum.',
        kind: { type: 'coins', min: 0, max: 1_000_000, zero: 'Card price only' },
      },
      {
        key: 'payouts.hourlyUsdBreaker',
        label: 'Hourly automatic-approval limit',
        help: 'Once automatic approvals in the last hour pass this amount, further ones are held for staff. Protects against a burst of fraud.',
        kind: { type: 'usd', min: 0, max: 100_000, zero: 'No limit' },
        danger: (from, to) =>
          to === 0
            ? 'With no limit, nothing stops a burst of automatic approvals.'
            : Number(to) > Number(from) * 2
              ? 'This more than doubles how much can go out automatically in an hour.'
              : null,
      },
    ],
  },
  {
    title: 'Claude payout review',
    blurb:
      'Claude reviews each cash-out with the risk rules. These are the limits it works within.',
    fields: [
      {
        key: 'ai.enabled',
        label: 'Claude reviews cash-outs',
        help: 'Off means every cash-out waits for staff.',
        kind: { type: 'toggle', on: 'On', off: 'Off' },
        danger: (_, to) => (to === false ? 'Every cash-out will wait for a person.' : null),
      },
      {
        key: 'ai.mode',
        label: 'Decisions',
        help: 'Enforced: Claude’s approve, hold or reject takes effect. Shadow: Claude only recommends and staff decide everything.',
        kind: { type: 'mode' },
      },
      {
        key: 'ai.autoApproveMaxUsd',
        label: 'Largest cash-out Claude may approve',
        help: 'Anything above this waits for staff, whatever Claude thinks.',
        kind: { type: 'usd', min: 0, max: 1000, zero: 'Claude never approves' },
        danger: (from, to) =>
          Number(to) > Number(from)
            ? 'Raises how much Claude can send without a person looking.'
            : null,
      },
      {
        key: 'ai.firstAutoApproveMaxUsd',
        label: 'Largest first cash-out Claude may approve',
        help: 'First cash-outs are where most fraud shows up, so this is usually lower.',
        kind: { type: 'usd', min: 0, max: 1000, zero: 'Claude never approves' },
        danger: (from, to) =>
          Number(to) > Number(from)
            ? 'Raises how much a brand-new account can receive without a person looking.'
            : null,
      },
      {
        key: 'ai.model',
        label: 'Model',
        help: 'Used for cash-out review.',
        kind: { type: 'model' },
      },
    ],
  },
  {
    title: 'Offer credits',
    blurb: 'Coins networks report through postbacks.',
    fields: [
      {
        key: 'postbacks.maxAutoCoins',
        label: 'Hold single credits above',
        help: 'One offer worth more than this is credited as pending and waits for staff to release it.',
        kind: { type: 'coins', min: 1000, max: 10_000_000 },
        danger: (from, to) =>
          Number(to) > Number(from) * 2
            ? 'Large credits will reach balances without anyone checking them.'
            : null,
      },
    ],
  },
  {
    title: 'Gift card pricing',
    blurb:
      'How a card’s cost becomes its price in coins. Per-card markups on the Gift cards page win.',
    fields: [
      {
        key: 'giftcards.markupPercent',
        label: 'Markup',
        help: 'Added on top of what WizzGift charges us.',
        kind: { type: 'percent', min: 0, max: 50 },
      },
      {
        key: 'giftcards.fxBufferPercent',
        label: 'Exchange-rate buffer',
        help: 'Extra margin on cards priced in another currency, so a rate move between pricing and ordering doesn’t cost us.',
        kind: { type: 'percent', min: 0, max: 50 },
        danger: (_, to) =>
          Number(to) < 1
            ? 'Below 1%, an ordinary rate move can make cards cost more than they earn.'
            : null,
      },
    ],
  },
  {
    title: 'Rewards',
    blurb: 'Coins we give away. Every coin here is real money at cash-out.',
    fields: [
      {
        key: 'rewards.signupBonus',
        label: 'Sign-up bonus',
        help: 'Paid when a new account verifies its email, so throwaway sign-ups can’t collect it.',
        kind: { type: 'coins', min: 0, max: 100_000, zero: 'None' },
        danger: (from, to) =>
          Number(to) > Number(from) ? 'Higher sign-up bonuses attract fake accounts.' : null,
      },
      {
        key: 'rewards.appBonus',
        label: 'Android app bonus',
        help: 'Paid once per account and once per phone, the first time someone is signed in through the app (new or existing users, email verified). Emulators and cloned apps don’t get it. The website shows “Get the app” with this amount.',
        kind: { type: 'coins', min: 0, max: 10_000, zero: 'None' },
        danger: (from, to) =>
          Number(to) > Number(from) ? 'A bigger app bonus is worth faking a phone for.' : null,
      },
      {
        key: 'rewards.notifyBonus',
        label: 'Notifications bonus (browsers)',
        help: 'Paid once per account and once per browser when someone turns on notifications on the website and taps the first one. Never in the Android app, where Google Play could read it as buying a permission.',
        kind: { type: 'coins', min: 0, max: 10_000, zero: 'None' },
      },
      {
        key: 'rewards.dailyBonus',
        label: 'Daily bonus by streak day',
        help: 'Day 1 is the first claim of a streak. Missing a day, or finishing the last one, starts again at day 1.',
        kind: { type: 'days' },
      },
      {
        key: 'referral.rateBps',
        label: 'Referral commission',
        help: 'Share of a referred user’s offer earnings paid to the person who invited them.',
        kind: { type: 'bps', max: 3000 },
      },
    ],
  },
  {
    title: 'Identity checks (Didit)',
    blurb:
      'Risky or larger cash-outs wait for a Didit check: ID photo, selfie and face match. Nobody reviews checks by hand: Claude settles the unclear ones.',
    fields: [
      {
        key: 'kyc.enabled',
        label: 'Ask for identity checks',
        help: 'Needs the DIDIT_API_KEY, DIDIT_WEBHOOK_SECRET and DIDIT_WORKFLOW_ID settings on the API Worker; without them nothing is asked.',
        kind: { type: 'toggle', on: 'On', off: 'Off' },
      },
      {
        key: 'kyc.requiredOverUsd',
        label: 'Check cash-outs from',
        help: 'Cash-outs of at least this amount need a passed check. Cash-outs the risk rules hold always do. A passed check covers a year.',
        kind: { type: 'usd', min: 0, max: 10_000, zero: 'Every cash-out' },
        danger: (from, to) =>
          Number(to) > Number(from) ? 'Fewer cash-outs will be checked.' : null,
      },
      {
        key: 'kyc.aiEnabled',
        label: 'Claude settles unclear checks',
        help: 'Checks Didit leaves in review: Claude declines or asks for a retake, never approves. Off declines them all.',
        kind: { type: 'toggle', on: 'On', off: 'Off' },
        danger: (_, to) =>
          to === false ? 'Every check Didit leaves in review will be declined.' : null,
      },
      {
        key: 'kyc.model',
        label: 'Identity check model',
        help: 'Used to settle checks Didit leaves in review.',
        kind: { type: 'model' },
      },
    ],
  },
  {
    title: 'Support and fraud assistants',
    blurb:
      'Claude answers tickets first, reviews risk flags and helps staff here in the admin. Its fraud reviews are recommendations only.',
    fields: [
      {
        key: 'support.aiEnabled',
        label: 'Claude answers support tickets',
        help: 'Off sends every new ticket straight to staff.',
        kind: { type: 'toggle', on: 'On', off: 'Off' },
      },
      {
        key: 'support.fastModel',
        label: 'Support model, first try',
        help: 'Answers every ticket first. When it isn’t sure, the model below answers instead. Set both to the same model to always use one.',
        kind: { type: 'model' },
      },
      {
        key: 'support.model',
        label: 'Support model, harder questions',
        help: 'Answers when the first model isn’t sure. Also translates support messages for staff and users.',
        kind: { type: 'model' },
      },
      {
        key: 'support.notes',
        label: 'What Claude should know',
        help: 'Rules and facts for answering tickets, on top of the FAQ and each user’s account and cash-out numbers, which it already sees. Write them as you would brief a new team member, one per line. Users never see this. Applies to the next reply.',
        kind: {
          type: 'text',
          max: 4000,
          rows: 8,
          placeholder:
            'e.g. Cash-outs to PayPal aren’t available; suggest a gift card.\nIf a user asks about the Android app, it’s on Google Play as “GaintPlay”.',
        },
      },
      {
        key: 'support.autoCloseHours',
        label: 'Close tickets the user doesn’t answer',
        help: 'A ticket waiting for the user closes after this many hours without a reply, with a short note from support and no email. A reply opens it again. Tickets on hold never close this way.',
        kind: { type: 'count', min: 0, max: 720, unit: 'hours', zero: 'Never' },
      },
      {
        key: 'fraud.aiEnabled',
        label: 'Claude reviews risk flags',
        help: 'Adds a recommendation and evidence to each new flag. Staff still decide.',
        kind: { type: 'toggle', on: 'On', off: 'Off' },
      },
      {
        key: 'fraud.model',
        label: 'Fraud model',
        help: 'Used to review risk flags.',
        kind: { type: 'model' },
      },
      {
        key: 'assistant.enabled',
        label: 'Admin assistant',
        help: 'The chat on every admin page, for staff whose role allows it. It looks things up as them and asks before changing anything. Off: it answers nothing, and changes it asked for can’t be approved.',
        kind: { type: 'toggle', on: 'On', off: 'Off' },
      },
      {
        key: 'assistant.model',
        label: 'Admin assistant model',
        help: 'Answers staff in the admin chat and prepares the changes they approve. Only models that think before answering (Opus, Sonnet and Haiku 5.5, Sonnet and Opus 5, Fable 5 and 5.1).',
        kind: { type: 'model' },
      },
      {
        key: 'assistant.dailyUsdPerStaff',
        label: 'Admin assistant budget',
        help: 'What one staff member’s assistant conversations may cost in 24 hours. When it’s used up, the assistant stops answering them until the oldest calls fall out of the window.',
        kind: { type: 'usd', min: 0, max: 1000, zero: 'No limit' },
      },
    ],
  },
  {
    title: 'Old accounts',
    blurb:
      'Connecting accounts from the old site. Only coins come over; the old role, ID checks and phone never do.',
    fields: [
      {
        key: 'legacy.claimDeadline',
        label: 'Linking closes',
        help: 'The last moment anyone can start or finish connecting an old account; users then see “Connecting old accounts ended on <date>.” 30 days later the approvals page goes away and unclaimed old accounts are deleted.',
        kind: { type: 'datetime' },
        danger: (from, to) =>
          Date.parse(String(to)) < Date.now()
            ? 'This closes linking right away.'
            : Date.parse(String(to)) < Date.parse(String(from))
              ? 'Closes linking earlier than announced.'
              : null,
      },
      {
        key: 'legacy.activeSince',
        label: 'Old account must be active since',
        help: 'The old account needs an earning or a withdrawal on or after this moment. Older ones are refused as deleted for inactivity.',
        kind: { type: 'datetime' },
      },
      {
        key: 'legacy.reviewOverCoins',
        label: 'Approve by hand above',
        help: 'A link that would bring over more than this many coins waits in Old account approvals and nothing is credited until staff approve. At or below it, the coins are credited at once.',
        kind: { type: 'coins', min: 0, max: 10_000_000, zero: 'Every link with coins' },
        danger: (from, to) =>
          Number(to) > Number(from)
            ? 'More old balances will be credited without a person looking.'
            : null,
      },
    ],
  },
  {
    title: 'Site',
    blurb: 'What every visitor sees.',
    fields: [
      {
        key: 'site.announcement',
        label: 'Announcement banner',
        help: 'Shown at the top of every page, signed in or not, within about a minute. Visitors can close it; a changed message shows again. Leave empty for none.',
        kind: { type: 'text', max: 300 },
      },
    ],
  },
  {
    title: 'App reviews',
    blurb:
      'In the Android app, Google’s own rating card shows right after someone closes the receipt of a cash-out that just arrived, if nothing went wrong for them in the last 14 days (no declined cash-out, no reversed reward, no open support conversation). Never for coins, never behind a question: Google forbids both. Google also limits how often the card appears.',
    fields: [
      {
        key: 'reviews.enabled',
        label: 'Ask for reviews',
        help: 'Off: the app never shows the rating card.',
        kind: { type: 'toggle', on: 'On', off: 'Off' },
      },
      {
        key: 'reviews.everyDays',
        label: 'Days between asks',
        help: 'The same account isn’t asked again sooner. Google may skip an ask within about a month anyway.',
        kind: { type: 'count', min: 30, max: 365, unit: 'days' },
      },
      {
        key: 'reviews.maxAsks',
        label: 'Asks per account',
        help: 'After this many, never again.',
        kind: { type: 'count', min: 1, max: 10, unit: 'times' },
      },
    ],
  },
  {
    title: 'Automatic restrictions',
    blurb:
      'Two fraud rules change an account by themselves, before anyone looks. Earnings from a country other than the ID document’s (a VPN or proxy): restricted, “VPN/Proxy used”. One Android phone on several accounts (same Android ID, or same advertising ID unless it’s all zeros) changes nothing until a second account on it asks to cash out while another has cashed out: that one is stopped and restricted, and all of them are blocked from the number below; the match is certain, so its flag comes already confirmed. The country rule raises a flag that Claude reviews within minutes; staff decide. A status you set back stays: a rule acts once per account and cause, and a flag you dismiss stops it for that account.',
    fields: [
      {
        key: 'risk.autoStatus',
        label: 'Rules',
        help: 'Enforced: the rules restrict and block. Shadow: they only raise the flag for Claude and staff.',
        kind: { type: 'mode' },
      },
      {
        key: 'risk.sharedPhoneBlockAt',
        label: 'Block when one phone has',
        help: 'Accounts on the same Android phone (Android ID or advertising ID) from which all of them are blocked. Fewer than this (two or more): restricted.',
        kind: { type: 'count', min: 3, max: 50, unit: 'accounts' },
      },
    ],
  },
  {
    title: 'Android app',
    blurb:
      'Make old versions of the Play Store app update. An app older than the build below opens a full-screen “Update GaintPlay” page with one button to Google Play, and the site doesn’t load behind it. Browsers are never affected. Raise it only once that build is out to everyone on Google Play (100% of the rollout), or people who can’t get it yet are locked out.',
    fields: [
      {
        key: 'app.minBuild',
        label: 'Oldest build allowed',
        help: 'The versionCode from Play Console (App bundle explorer), e.g. 32. Apps before build 32 don’t send their build and count as older than any number. 0 = off. Takes effect within about a minute, the next time the app opens.',
        kind: { type: 'count', min: 0, max: 1_000_000, unit: '', zero: 'Off' },
      },
    ],
  },
  {
    title: 'New-member guide',
    blurb:
      'Every new account gets a short walkthrough after signing up: pick an offer, finish it, get the coins, cash out. Full screen on phones, a window on desktop, and it ends on the Earn page. Anyone can open it again from “Guide me” in the site’s footer. A video in the visitor’s language closes it, with only Pause and Skip.',
    fields: [
      {
        key: 'guide.videos',
        label: 'Videos by language',
        help: 'Paste each video’s Bunny Stream link: in Bunny, Stream → the library → the video, its direct play link (https://player.mediadelivery.net/play/<library id>/<video id>) or its embed code. Phones play the portrait video full screen; desktop plays the landscape one in the guide’s window. A language with only one gets it on both, and one with neither ends the guide without a video. Turn Bunny’s own player controls off in the library’s Player settings: we show Pause and Skip.',
        kind: { type: 'guideVideos' },
        wide: true,
        danger: (from, to) => {
          const has = (v: Value, l: (typeof LOCALES)[number]) =>
            GUIDE_VIDEO_KINDS.some((k) => (v as GuideVideos)[l]?.[k]);
          const lost = LOCALES.filter((l) => has(from, l) && !has(to, l));
          return lost.length
            ? `${lost.map((l) => LOCALE_INFO[l].english).join(', ')} will end the guide without a video`
            : null;
        },
      },
    ],
  },
  {
    title: 'Community',
    blurb:
      'Links shown under “Join the community” on the Rewards page. No coins for joining: X, Reddit, Discord and YouTube all forbid paying for follows, joins or subscriptions. Leave a link empty to hide it.',
    fields: [
      {
        key: 'community.discord',
        label: 'Discord invite',
        help: 'An invite link to the server.',
        kind: {
          type: 'link',
          placeholder: 'https://discord.gg/…',
          hosts: ['discord.gg', 'discord.com'],
        },
      },
      {
        key: 'community.telegram',
        label: 'Telegram channel',
        help: 'The channel’s public link.',
        kind: { type: 'link', placeholder: 'https://t.me/…', hosts: ['t.me', 'telegram.me'] },
      },
      {
        key: 'community.x',
        label: 'X (Twitter)',
        help: 'The profile link.',
        kind: { type: 'link', placeholder: 'https://x.com/…', hosts: ['x.com', 'twitter.com'] },
      },
      {
        key: 'community.reddit',
        label: 'Reddit',
        help: 'The subreddit link.',
        kind: {
          type: 'link',
          placeholder: 'https://www.reddit.com/r/…',
          hosts: ['reddit.com', 'www.reddit.com'],
        },
      },
      {
        key: 'community.youtube',
        label: 'YouTube channel',
        help: 'The channel link: youtube.com/@handle or youtube.com/channel/….',
        kind: {
          type: 'link',
          placeholder: 'https://www.youtube.com/@…',
          hosts: ['youtube.com', 'www.youtube.com', 'm.youtube.com'],
        },
      },
    ],
  },
];
const FIELDS = new Map(GROUPS.flatMap((g) => g.fields.map((f) => [f.key, f] as const)));

/** Problem with a draft value, mirroring the server's rules so mistakes show before saving. */
function problem(f: Field, v: Value): string | null {
  const k = f.kind;
  if (k.type === 'coins' || k.type === 'bps' || k.type === 'count') {
    const max = k.max;
    const min = k.type === 'bps' ? 0 : k.min;
    if (typeof v !== 'number' || !Number.isInteger(v) || v < min || v > max)
      return k.type === 'bps'
        ? `Between 0% and ${max / 100}%`
        : `A whole number from ${min.toLocaleString()} to ${max.toLocaleString()}`;
  }
  if (k.type === 'usd' || k.type === 'percent')
    if (typeof v !== 'number' || !Number.isFinite(v) || v < k.min || v > k.max)
      return `Between ${k.min} and ${k.max.toLocaleString()}`;
  if (k.type === 'days') {
    const d = v as number[];
    if (!d.length || d.length > 31) return 'Between 1 and 31 days';
    if (d.some((n) => !Number.isInteger(n) || n < 0 || n > 100_000))
      return 'Each day is a whole number from 0 to 100,000';
  }
  if (k.type === 'model' && !/^claude-[a-z0-9.-]{3,60}$/.test(String(v)))
    return 'A Claude model id, like claude-opus-5-5';
  if (k.type === 'text' && String(v).length > k.max) return `At most ${k.max} characters`;
  if (k.type === 'link' && v !== '' && !linkOn(String(v), k.hosts))
    return `An https link on ${k.hosts.filter((h) => !h.startsWith('m.')).join(' or ')}`;
  if (k.type === 'guideVideos') {
    const bad = videoLinks(v as GuideVideos).filter((l) => !bunnyVideo(l.link));
    if (bad.length)
      return `${bad.map((l) => `${LOCALE_INFO[l.lang].english} ${l.kind}`).join(', ')}: not a Bunny Stream link`;
  }
  if (
    k.type === 'datetime' &&
    !(ISO_WITH_OFFSET.test(String(v)) && Number.isFinite(Date.parse(String(v))))
  )
    return 'A date and time with its offset, like 2026-11-30T23:59:59+05:45';
  return null;
}

/** An https link on one of `hosts`, at most 200 characters (the server's communityLink rule). */
function linkOn(v: string, hosts: string[]): boolean {
  try {
    const u = new URL(v);
    return u.protocol === 'https:' && hosts.includes(u.hostname) && v.length <= 200;
  } catch {
    return false;
  }
}

/** Same rule as the server: the offset is part of the value, so nobody's time zone is guessed. */
const ISO_WITH_OFFSET = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,3})?)?(Z|[+-]\d{2}:\d{2})$/;

/** "30 Nov 2026, 23:59 Nepal time" for an ISO moment. */
function nepalTime(iso: string): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return iso;
  return `${new Date(t).toLocaleString('en-GB', {
    timeZone: 'Asia/Kathmandu',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })} Nepal time`;
}

function show(f: Field, v: Value): string {
  const k = f.kind;
  if (k.type === 'toggle') return v ? k.on : k.off;
  if (k.type === 'mode') return v === 'enforced' ? 'Enforced' : 'Shadow';
  if (k.type === 'coins')
    return v === 0 && k.zero
      ? k.zero
      : `${Number(v).toLocaleString()} coins (${fmtUsd(Number(v) / COINS_PER_USD)})`;
  if (k.type === 'count')
    return v === 0 && k.zero ? k.zero : `${Number(v).toLocaleString()} ${k.unit}`.trim();
  if (k.type === 'usd') return v === 0 && k.zero ? k.zero : fmtUsd(Number(v));
  if (k.type === 'percent') return `${v}%`;
  if (k.type === 'bps') return `${Number(v) / 100}%`;
  if (k.type === 'days') return (v as number[]).join(' · ');
  if (k.type === 'text') {
    // Long notes are summarised: the save dialog shows the old and the new value side by side.
    const t = String(v);
    if (!t) return 'None';
    return t.length > 120
      ? `“${t.slice(0, 100).trimEnd()}…” (${t.length.toLocaleString()} characters)`
      : `“${t}”`;
  }
  if (k.type === 'datetime') return nepalTime(String(v));
  if (k.type === 'link') return v ? String(v) : 'None (hidden)';
  if (k.type === 'guideVideos') {
    const by = LOCALES.flatMap((lang) => {
      const kinds = videoLinks(v as GuideVideos)
        .filter((l) => l.lang === lang)
        .map((l) => l.kind);
      return kinds.length ? [`${LOCALE_INFO[lang].english} (${kinds.join(' + ')})`] : [];
    });
    return by.length ? by.join(', ') : 'No videos';
  }
  if (k.type === 'model')
    return MODELS.find(([m]) => m === v)?.[1].replace(/ \(.*\)$/, '') ?? String(v);
  return String(v);
}

const same = (a: Value, b: Value) => JSON.stringify(a) === JSON.stringify(b);

export function Settings() {
  const qc = useQueryClient();
  // Without settings.manage the page is read-only: every field sits in a disabled fieldset.
  const canEdit = useCan()('settings.manage');
  const { data, error, refetch } = useQuery({
    queryKey: ['settings'],
    queryFn: () => api<SettingsData>('/settings'),
  });
  const [draft, setDraft] = useState<Record<string, Value>>({});
  const [saving, setSaving] = useState(false);
  const changes = useMemo(
    () =>
      data ? Object.entries(draft).filter(([k, v]) => !same(v, data.settings[k] as Value)) : [],
    [draft, data],
  );
  const invalid = changes.filter(([k, v]) => problem(FIELDS.get(k)!, v));

  const header = (
    <PageHeader
      title="Settings"
      description={
        canEdit
          ? 'Changes apply to the live site as soon as you save. Each one is recorded in the audit log with your name.'
          : 'What the live site runs with. Changing settings isn’t part of your roles, so you can read them here but not edit.'
      }
    />
  );
  if (error && !data)
    return (
      <div className="space-y-6">
        {header}
        <ErrorNote error={error} onRetry={() => void refetch()} />
      </div>
    );
  if (!data)
    return (
      <div className="space-y-6" aria-busy="true">
        {header}
        {[3, 4, 2].map((rows, i) => (
          <div key={i} className="card overflow-hidden">
            <div className="space-y-2 border-b border-hairline px-5 py-4">
              <div className="h-4 w-40 animate-pulse rounded bg-ink/[0.08]" />
              <div className="h-3 w-72 max-w-full animate-pulse rounded bg-ink/[0.05]" />
            </div>
            {Array.from({ length: rows }, (_, j) => (
              <div
                key={j}
                className="grid gap-3 border-b border-hairline px-5 py-4 last:border-b-0 md:grid-cols-[minmax(0,1fr)_minmax(0,340px)] md:gap-8"
              >
                <div className="space-y-2">
                  <div className="h-3.5 w-1/2 animate-pulse rounded bg-ink/[0.07]" />
                  <div className="h-3 w-3/4 animate-pulse rounded bg-ink/[0.05]" />
                </div>
                <div className="h-11 animate-pulse rounded-[10px] bg-ink/[0.05]" />
              </div>
            ))}
          </div>
        ))}
      </div>
    );

  const meta = new Map(data.changed.map((c) => [c.key, c]));
  const value = (k: string) => (k in draft ? draft[k]! : data.settings[k]!);
  const setValue = (k: string, v: Value) =>
    setDraft((d) => {
      const n = { ...d };
      if (same(v, data.settings[k]!)) delete n[k];
      else n[k] = v;
      return n;
    });

  async function save() {
    const warnings = changes
      .map(([k, v]) => FIELDS.get(k)!.danger?.(data!.settings[k]!, v))
      .filter(Boolean);
    const ok = await confirmAction({
      title: `Save ${changes.length} change${changes.length === 1 ? '' : 's'} to the live site?`,
      tone: warnings.length ? 'danger' : 'default',
      confirmLabel: warnings.length
        ? `Save anyway`
        : `Save ${changes.length === 1 ? 'change' : `${changes.length} changes`}`,
      body: (
        <div className="space-y-3">
          <ul className="divide-y divide-hairline overflow-hidden rounded-[12px] border border-hairline bg-paper text-[13px]">
            {changes.map(([k, v]) => {
              const f = FIELDS.get(k)!;
              const warn = f.danger?.(data!.settings[k]!, v);
              return (
                <li key={k} className="px-3.5 py-2.5">
                  <div className="font-semibold text-ink">{f.label}</div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 tabular-nums">
                    <span className="text-stone line-through decoration-ink/30">
                      {show(f, data!.settings[k]!)}
                    </span>
                    <ArrowRight size={13} className="text-stone" aria-label="becomes" />
                    <b className="font-bold text-ink">{show(f, v)}</b>
                  </div>
                  {warn && (
                    <div className="mt-1.5 flex items-start gap-1.5 rounded-[8px] bg-sun/25 px-2 py-1.5 text-[12px] font-semibold text-ink">
                      <TriangleAlert size={13} className="mt-px shrink-0" aria-hidden="true" />
                      {warn}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
          <p className="text-[12px] text-stone">
            Takes effect immediately and is recorded in the audit log with your name.
          </p>
        </div>
      ),
    });
    if (ok === null) return;
    setSaving(true);
    const failed: string[] = [];
    for (const [key, v] of changes) {
      try {
        await api('/settings', { method: 'PUT', body: { key, value: v } });
        setDraft((d) => {
          const n = { ...d };
          delete n[key];
          return n;
        });
      } catch (e) {
        failed.push(`${FIELDS.get(key)!.label}: ${(e as Error).message}`);
      }
    }
    setSaving(false);
    await qc.invalidateQueries({ queryKey: ['settings'] });
    if (failed.length) toast(failed.join('\n'), 'error');
    else toast('Settings saved.');
  }

  const warnings = changes.filter(([k, v]) => FIELDS.get(k)!.danger?.(data.settings[k]!, v));

  return (
    <div className="space-y-6">
      {header}
      {GROUPS.map((g) => (
        <fieldset
          key={g.title}
          disabled={!canEdit}
          className="card min-w-0 overflow-hidden"
          aria-labelledby={`grp-${g.title}`}
        >
          <header className="border-b border-hairline px-4 py-4 sm:px-5">
            <h2 id={`grp-${g.title}`} className="text-[17px] font-extrabold tracking-[-0.01em]">
              {g.title}
            </h2>
            <p className="mt-0.5 text-[13px] text-stone">{g.blurb}</p>
          </header>
          <div className="divide-y divide-hairline">
            {g.fields.map((f) => {
              const v = value(f.key);
              const edited = f.key in draft;
              const err = edited ? problem(f, v) : null;
              const warn = edited && !err ? f.danger?.(data.settings[f.key]!, v) : null;
              const m = meta.get(f.key);
              const isDefault = same(v, data.defaults[f.key]!);
              return (
                <div
                  key={f.key}
                  className={`relative grid gap-3 px-4 py-4 sm:px-5 ${f.wide ? 'md:gap-4' : 'md:grid-cols-[minmax(0,1fr)_minmax(0,340px)] md:gap-8'} ${
                    edited
                      ? 'bg-[#fbf8f3] before:absolute before:inset-y-0 before:left-0 before:w-[3px] before:bg-ink'
                      : ''
                  }`}
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <label
                        htmlFor={f.key}
                        id={`${f.key}-label`}
                        className="text-[15px] font-bold leading-snug"
                      >
                        {f.label}
                      </label>
                      {edited && (
                        <span className="rounded-full bg-ink px-2 py-px text-[11px] font-bold text-white">
                          Edited
                        </span>
                      )}
                    </div>
                    {f.help && (
                      <p
                        id={`${f.key}-help`}
                        className="mt-1 max-w-prose text-[13px] leading-snug text-stone"
                      >
                        {f.help}
                      </p>
                    )}
                    {(edited || m || !isDefault) && (
                      <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-stone">
                        {edited ? (
                          <>
                            <span>
                              Was{' '}
                              <b className="font-semibold text-graphite">
                                {show(f, data.settings[f.key]!)}
                              </b>
                            </span>
                            <button
                              type="button"
                              className="-my-1 inline-flex items-center gap-1 rounded-[6px] px-1 py-1 max-sm:min-h-9 font-semibold text-graphite underline decoration-ink/25 underline-offset-2 transition hover:text-ink hover:decoration-ink"
                              onClick={() => setValue(f.key, data.settings[f.key]!)}
                            >
                              Undo
                            </button>
                          </>
                        ) : (
                          m && (
                            <span>
                              Changed by {actorLabel(m.updatedBy, data.staffNames)}{' '}
                              <TimeAgo t={m.updatedAt} />
                            </span>
                          )
                        )}
                        {!isDefault &&
                          !(edited && same(data.settings[f.key]!, data.defaults[f.key]!)) && (
                            <button
                              type="button"
                              className="-my-1 inline-flex items-center gap-1 rounded-[6px] px-1 py-1 max-sm:min-h-9 font-semibold text-graphite transition hover:text-ink"
                              onClick={() => setValue(f.key, data.defaults[f.key]!)}
                            >
                              <RotateCcw size={12} aria-hidden="true" /> Reset to default (
                              {show(f, data.defaults[f.key]!)})
                            </button>
                          )}
                      </p>
                    )}
                  </div>
                  <div className="min-w-0">
                    <Control f={f} v={v} invalid={!!err} onChange={(x) => setValue(f.key, x)} />
                    {err && (
                      <p
                        role="alert"
                        className="mt-1.5 flex items-start gap-1.5 text-[12px] font-semibold text-berry-deep"
                      >
                        <CircleAlert size={13} className="mt-px shrink-0" aria-hidden="true" />
                        {err}
                      </p>
                    )}
                    {warn && (
                      <p className="mt-1.5 flex items-start gap-1.5 rounded-[8px] bg-sun/25 px-2.5 py-1.5 text-[12px] font-semibold text-ink">
                        <TriangleAlert size={13} className="mt-px shrink-0" aria-hidden="true" />
                        {warn}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </fieldset>
      ))}

      {changes.length > 0 && (
        <div
          role="region"
          aria-label="Unsaved changes"
          className="sticky bottom-3 z-30 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-[16px] border-[1.5px] border-ink bg-paper px-4 py-3 shadow-[4px_4px_0_#111110]"
          style={{ animation: 'dialog-in 160ms ease-out' }}
        >
          <div className="min-w-0 basis-full text-[14px] sm:flex-1 sm:basis-auto">
            <div className="font-bold">
              {changes.length} unsaved change{changes.length === 1 ? '' : 's'}
            </div>
            <div className="text-[12px] text-stone">
              {invalid.length > 0 ? (
                <span className="font-semibold text-berry-deep">
                  Fix {invalid.length} highlighted value{invalid.length === 1 ? '' : 's'} first
                </span>
              ) : warnings.length > 0 ? (
                <span className="font-semibold text-graphite">
                  {warnings.length} {warnings.length === 1 ? 'needs' : 'need'} a second look
                </span>
              ) : (
                'Nothing is live until you save.'
              )}
            </div>
          </div>
          <div className="flex flex-1 gap-2 sm:flex-none">
            <button
              className="btn-ghost h-10 px-4 py-0 text-[14px]"
              onClick={() => setDraft({})}
              disabled={saving}
            >
              Discard
            </button>
            <button
              className="btn-primary h-10 flex-1 px-4 py-0 text-[14px] sm:flex-none"
              onClick={save}
              disabled={saving || invalid.length > 0}
            >
              {saving ? 'Saving…' : 'Review and save'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Control({
  f,
  v,
  invalid,
  onChange,
}: {
  f: Field;
  v: Value;
  invalid: boolean;
  onChange: (v: Value) => void;
}) {
  const k = f.kind;
  const described = f.help ? `${f.key}-help` : undefined;
  if (k.type === 'toggle')
    return (
      <Toggle
        id={f.key}
        on={v === true}
        labels={[k.off, k.on]}
        describedBy={described}
        onChange={(b) => onChange(b)}
      />
    );
  if (k.type === 'mode')
    return (
      <Segmented
        id={f.key}
        value={String(v)}
        options={[
          ['enforced', 'Enforced'],
          ['shadow', 'Shadow'],
        ]}
        onChange={onChange}
      />
    );
  const box = { id: f.key, invalid, describedBy: described };
  if (k.type === 'coins')
    return (
      <NumberBox
        {...box}
        value={v as number}
        onChange={onChange}
        suffix="coins"
        step={1}
        note={(n) => (n === 0 && k.zero ? k.zero : `= ${fmtUsd(n / COINS_PER_USD)}`)}
      />
    );
  if (k.type === 'count')
    return (
      <NumberBox
        {...box}
        value={v as number}
        onChange={onChange}
        suffix={k.unit}
        step={1}
        note={(n) => (n === 0 && k.zero ? k.zero : null)}
      />
    );
  if (k.type === 'usd')
    return (
      <NumberBox
        {...box}
        value={v as number}
        onChange={onChange}
        prefix="$"
        step={0.01}
        note={(n) => (n === 0 && k.zero ? k.zero : null)}
      />
    );
  if (k.type === 'percent')
    return <NumberBox {...box} value={v as number} onChange={onChange} suffix="%" step={0.1} />;
  if (k.type === 'bps')
    return (
      <NumberBox
        {...box}
        value={(v as number) / 100}
        onChange={(n) => onChange(Math.round(n * 100))}
        suffix="%"
        step={0.01}
        note={(n) => `${fmtNum(Math.round(n * 100))} basis points`}
      />
    );
  if (k.type === 'model')
    return <ModelPicker id={f.key} value={String(v)} invalid={invalid} onChange={onChange} />;
  if (k.type === 'days') return <DaysEditor id={f.key} value={v as number[]} onChange={onChange} />;
  if (k.type === 'guideVideos')
    return <GuideVideosEditor id={f.key} value={v as GuideVideos} onChange={onChange} />;
  if (k.type === 'link')
    return (
      <div
        className={`flex h-11 items-center rounded-[10px] border bg-paper transition focus-within:shadow-[0_0_0_3px_var(--color-coin-tint)] ${
          invalid ? 'border-berry-deep' : 'border-hairline focus-within:border-ink'
        }`}
      >
        <input
          id={f.key}
          inputMode="url"
          spellCheck={false}
          autoComplete="off"
          aria-describedby={described}
          aria-invalid={invalid || undefined}
          className="h-full w-full min-w-0 rounded-[10px] bg-transparent px-3.5 text-[14px] outline-none placeholder:text-stone focus-visible:outline-none"
          placeholder={k.placeholder}
          value={String(v)}
          onChange={(e) => onChange(e.target.value.trim())}
        />
        {v !== '' && !invalid && (
          <a
            href={String(v)}
            target="_blank"
            rel="noreferrer noopener"
            className="mr-1 inline-flex h-8 shrink-0 items-center gap-1 rounded-[8px] px-2 text-[12px] font-semibold text-graphite transition hover:bg-ink/[0.06] hover:text-ink"
            aria-label={`Open the ${f.label} link`}
          >
            Open <ExternalLink size={12} aria-hidden="true" />
          </a>
        )}
      </div>
    );
  if (k.type === 'datetime')
    return (
      <div>
        <input
          id={f.key}
          aria-describedby={described}
          aria-invalid={invalid || undefined}
          className={`input h-11 py-0 font-mono text-[13px] ${invalid ? '!border-berry-deep' : ''}`}
          value={String(v)}
          placeholder="2026-11-30T23:59:59+05:45"
          spellCheck={false}
          autoComplete="off"
          onChange={(e) => onChange(e.target.value.trim())}
        />
        {!invalid && <p className="mt-1.5 text-[12px] text-stone">= {nepalTime(String(v))}</p>}
      </div>
    );
  return (
    <div>
      <textarea
        id={f.key}
        aria-describedby={described}
        className="input min-h-24 py-2.5 text-[14px] leading-relaxed"
        value={String(v)}
        maxLength={k.max}
        rows={k.rows}
        placeholder={k.placeholder ?? 'No announcement'}
        onChange={(e) => onChange(e.target.value)}
      />
      <div className="mt-1 text-right text-[12px] tabular-nums text-stone">
        {String(v).length} / {k.max}
      </div>
    </div>
  );
}

function Toggle({
  id,
  on,
  labels,
  describedBy,
  onChange,
}: {
  id: string;
  on: boolean;
  labels: [string, string];
  describedBy?: string | undefined;
  onChange: (b: boolean) => void;
}) {
  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={on}
      aria-describedby={describedBy}
      onClick={() => onChange(!on)}
      className="group inline-flex min-h-10 items-center gap-3 rounded-full pr-2 md:-mt-1.5"
    >
      <span
        className={`relative h-7 w-12 shrink-0 rounded-full transition ${on ? 'bg-mint' : 'bg-ink/25 group-hover:bg-ink/35'}`}
      >
        <span
          className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow-[0_1px_2px_rgba(17,17,16,0.25)] transition-all ${on ? 'left-6' : 'left-1'}`}
        />
      </span>
      <span className={`text-[14px] font-bold ${on ? 'text-mint-deep' : 'text-graphite'}`}>
        {on ? labels[1] : labels[0]}
      </span>
    </button>
  );
}

function Segmented({
  id,
  value,
  options,
  onChange,
}: {
  id: string;
  value: string;
  options: [string, string][];
  onChange: (v: string) => void;
}) {
  return (
    <div
      id={id}
      className="inline-flex rounded-[12px] bg-ink/[0.06] p-1"
      role="radiogroup"
      aria-labelledby={`${id}-label`}
    >
      {options.map(([k, label]) => (
        <button
          key={k}
          type="button"
          role="radio"
          aria-checked={value === k}
          onClick={() => onChange(k)}
          className={`h-9 rounded-[9px] px-4 text-[14px] font-semibold transition max-sm:h-10 ${
            value === k
              ? 'bg-paper text-ink shadow-[0_1px_0_#e2ddd2,0_1px_3px_rgba(17,17,16,0.08)]'
              : 'text-graphite hover:text-ink'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

/** Number input that keeps what was typed (so "1." or "" don't jump) and reports parsed numbers. */
function NumberBox({
  id,
  value,
  onChange,
  prefix,
  suffix,
  step,
  note,
  invalid,
  describedBy,
}: {
  id: string;
  value: number;
  onChange: (n: number) => void;
  prefix?: string;
  suffix?: string;
  step: number;
  note?: (n: number) => ReactNode;
  invalid?: boolean;
  describedBy?: string | undefined;
}) {
  const [text, setText] = useState<string | null>(null);
  // Grouped digits while reading ("100,000"); the raw number while typing.
  const shown =
    text ??
    (Number.isFinite(value) ? value.toLocaleString('en-US', { maximumFractionDigits: 6 }) : '');
  const extra = note?.(value);
  return (
    <div>
      <div
        className={`flex h-11 items-center rounded-[10px] border bg-paper transition focus-within:shadow-[0_0_0_3px_var(--color-coin-tint)] ${
          invalid ? 'border-berry-deep' : 'border-hairline focus-within:border-ink'
        }`}
      >
        {prefix && (
          <span className="pl-3.5 text-[15px] font-semibold text-stone" aria-hidden="true">
            {prefix}
          </span>
        )}
        <input
          id={id}
          inputMode="decimal"
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          className={`h-full w-full min-w-0 rounded-[10px] bg-transparent text-[15px] font-semibold tabular-nums outline-none focus-visible:outline-none ${prefix ? 'pl-1.5' : 'pl-3.5'} ${suffix ? 'pr-2' : 'pr-3.5'}`}
          value={shown}
          onFocus={() => setText(Number.isFinite(value) ? String(value) : '')}
          onChange={(e) => {
            const t = e.target.value.replace(/[^\d.]/g, '');
            setText(t);
            const n = t === '' ? NaN : Number(t);
            onChange(Number.isFinite(n) ? (step >= 1 ? Math.trunc(n) : n) : NaN);
          }}
          onBlur={() => setText(null)}
        />
        {suffix && (
          <span className="pr-3.5 text-[14px] font-semibold text-stone" aria-hidden="true">
            {suffix}
          </span>
        )}
      </div>
      {extra && Number.isFinite(value) && (
        <p className="mt-1.5 text-[12px] tabular-nums text-stone">{extra}</p>
      )}
    </div>
  );
}

function ModelPicker({
  id,
  value,
  invalid,
  onChange,
}: {
  id: string;
  value: string;
  invalid: boolean;
  onChange: (v: string) => void;
}) {
  const known = MODELS.some(([m]) => m === value);
  const [open, setCustom] = useState(false);
  // An undo or reset back to a listed model closes the free-text box again.
  useEffect(() => {
    if (known) setCustom(false);
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  const custom = open || !known;
  return (
    <div className="space-y-2">
      <select
        id={id}
        className="input h-11 py-0 text-[14px]"
        value={custom ? '__custom' : value}
        onChange={(e) => {
          if (e.target.value === '__custom') setCustom(true);
          else {
            setCustom(false);
            onChange(e.target.value);
          }
        }}
      >
        {MODELS.map(([m, label]) => (
          <option key={m} value={m}>
            {label}
          </option>
        ))}
        <option value="__custom">Another model id…</option>
      </select>
      {custom && (
        <div>
          <label htmlFor={`${id}-custom`} className="label">
            Model id
          </label>
          <input
            id={`${id}-custom`}
            aria-invalid={invalid || undefined}
            className={`input h-11 py-0 font-mono text-[13px] ${invalid ? '!border-berry-deep' : ''}`}
            value={value}
            placeholder="claude-…"
            spellCheck={false}
            autoComplete="off"
            onChange={(e) => onChange(e.target.value.trim())}
          />
        </div>
      )}
    </div>
  );
}

/** Every link set in a guide.videos value, in the site's language order. */
function videoLinks(v: GuideVideos) {
  return LOCALES.flatMap((lang) =>
    GUIDE_VIDEO_KINDS.flatMap((kind) => {
      const link = v[lang]?.[kind] ?? '';
      return link ? [{ lang, kind, link }] : [];
    }),
  );
}

/** The value with `lang`'s `kind` link set: empty links and languages dropped, a stable order. */
function withVideo(
  v: GuideVideos,
  lang: (typeof LOCALES)[number],
  kind: GuideVideoKind,
  link: string,
) {
  const out: GuideVideos = {};
  for (const l of LOCALES) {
    const entry: Partial<Record<GuideVideoKind, string>> = {};
    for (const k of GUIDE_VIDEO_KINDS) {
      const x = l === lang && k === kind ? link : (v[l]?.[k] ?? '');
      if (x) entry[k] = x;
    }
    if (Object.keys(entry).length) out[l] = entry;
  }
  return out;
}

/** What staff paste: a link, or Bunny's whole embed code (its src is the link). */
function pastedLink(text: string): string {
  const src = text.match(/\ssrc=["']([^"']+)["']/i);
  return (src ? src[1]! : text).trim();
}

const KIND_INFO: Record<GuideVideoKind, { label: string; shape: string; Icon: typeof Smartphone }> =
  {
    phone: { label: 'Phone', shape: 'portrait, full screen', Icon: Smartphone },
    desktop: { label: 'Desktop', shape: 'landscape, in the window', Icon: Monitor },
  };

/**
 * A phone and a desktop video for each of the site's languages: one row per language, a link box
 * per kind that says whether it's a Bunny link and opens the video in Bunny's player to check it.
 */
function GuideVideosEditor({
  id,
  value,
  onChange,
}: {
  id: string;
  value: GuideVideos;
  onChange: (v: GuideVideos) => void;
}) {
  const set = videoLinks(value);
  const languages = new Set(set.map((l) => l.lang)).size;
  return (
    <div id={id} role="group" aria-labelledby={`${id}-label`} aria-describedby={`${id}-help`}>
      <p className="mb-2.5 text-[13px] text-stone">
        <b className="font-semibold text-ink tabular-nums">
          {languages} of {LOCALES.length} languages
        </b>{' '}
        have a video
        {languages > 0 && languages < LOCALES.length && '; the others end the guide without one'}.
      </p>
      <div className="overflow-hidden rounded-[12px] border border-hairline">
        <div className="hidden grid-cols-[180px_minmax(0,1fr)_minmax(0,1fr)] gap-4 border-b border-hairline bg-canvas px-4 py-2 text-[12px] font-semibold text-stone md:grid">
          <span>Language</span>
          {GUIDE_VIDEO_KINDS.map((kind) => {
            const { label, shape, Icon } = KIND_INFO[kind];
            return (
              <span key={kind} className="flex items-center gap-1.5">
                <Icon size={13} aria-hidden="true" />
                {label} <span className="font-normal">· {shape}</span>
              </span>
            );
          })}
        </div>
        <ul className="divide-y divide-hairline">
          {LOCALES.map((lang) => {
            const info = LOCALE_INFO[lang];
            const has = GUIDE_VIDEO_KINDS.filter((k) => value[lang]?.[k]);
            return (
              <li
                key={lang}
                className="grid gap-2.5 px-4 py-3 md:grid-cols-[180px_minmax(0,1fr)_minmax(0,1fr)] md:items-start md:gap-4"
              >
                <div className="flex items-center gap-2 md:min-h-10">
                  <span className="w-7 shrink-0 rounded-[6px] bg-ink/[0.06] py-0.5 text-center font-mono text-[11px] font-semibold uppercase text-graphite">
                    {lang}
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-[14px] font-semibold leading-tight">
                      {info.english}
                    </span>
                    {info.native !== info.english && (
                      <span className="block truncate text-[12px] leading-tight text-stone">
                        {info.native}
                      </span>
                    )}
                  </span>
                  {has.length === 1 && (
                    <span className="ml-auto shrink-0 rounded-full bg-sun/30 px-2 py-px text-[11px] font-semibold text-ink md:hidden">
                      {KIND_INFO[has[0]!].label} only
                    </span>
                  )}
                </div>
                {GUIDE_VIDEO_KINDS.map((kind) => (
                  <VideoLink
                    key={kind}
                    id={`${id}-${lang}-${kind}`}
                    lang={info.english}
                    kind={kind}
                    link={value[lang]?.[kind] ?? ''}
                    fallback={!value[lang]?.[kind] && has.length === 1}
                    onChange={(link) => onChange(withVideo(value, lang, kind, link))}
                  />
                ))}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

function VideoLink({
  id,
  lang,
  kind,
  link,
  fallback,
  onChange,
}: {
  id: string;
  lang: string;
  kind: GuideVideoKind;
  link: string;
  /** Empty while the language's other video is set: that one plays here too. */
  fallback: boolean;
  onChange: (link: string) => void;
}) {
  const video = link ? bunnyVideo(link) : null;
  const bad = !!link && !video;
  const { label, Icon } = KIND_INFO[kind];
  // Every link starts the same (host, library), so while not editing, the box shows which video.
  const [editing, setEditing] = useState(false);
  const shown =
    video && !editing ? `Video ${video.video.slice(0, 8)}…${video.video.slice(-4)}` : link;
  return (
    <div className="min-w-0">
      <label
        htmlFor={id}
        className="mb-1 flex items-center gap-1.5 text-[12px] font-semibold text-stone md:sr-only"
      >
        <Icon size={13} aria-hidden="true" />
        {label}
        <span className="sr-only"> video, {lang}</span>
      </label>
      <div
        className={`flex h-10 items-center rounded-[10px] border bg-paper transition focus-within:shadow-[0_0_0_3px_var(--color-coin-tint)] ${
          bad ? 'border-berry-deep' : 'border-hairline focus-within:border-ink'
        }`}
      >
        <input
          id={id}
          inputMode="url"
          spellCheck={false}
          autoComplete="off"
          aria-invalid={bad || undefined}
          aria-describedby={bad ? `${id}-error` : undefined}
          className="h-full w-full min-w-0 rounded-[10px] bg-transparent pl-3 pr-2 font-mono text-[12px] outline-none placeholder:font-sans placeholder:text-[13px] focus-visible:outline-none"
          placeholder={
            fallback ? `Plays the ${kind === 'phone' ? 'desktop' : 'phone'} video` : 'No video'
          }
          title={link || undefined}
          value={shown}
          onFocus={() => setEditing(true)}
          onBlur={() => setEditing(false)}
          onChange={(e) => onChange(pastedLink(e.target.value))}
        />
        {video && (
          <a
            href={`https://player.mediadelivery.net/play/${video.library}/${video.video}`}
            target="_blank"
            rel="noreferrer noopener"
            className="mr-1 inline-flex h-8 shrink-0 items-center gap-1 rounded-[8px] px-2 text-[12px] font-semibold text-graphite transition hover:bg-ink/[0.06] hover:text-ink"
            aria-label={`Watch the ${lang} ${kind} video in Bunny`}
          >
            Watch <ExternalLink size={12} aria-hidden="true" />
          </a>
        )}
      </div>
      {bad && (
        <p id={`${id}-error`} className="mt-1 text-[12px] font-semibold text-berry-deep">
          Not a Bunny Stream video link
        </p>
      )}
    </div>
  );
}

function DaysEditor({
  id,
  value,
  onChange,
}: {
  id: string;
  value: number[];
  onChange: (v: number[]) => void;
}) {
  const total = value.reduce((a, b) => a + (Number.isFinite(b) ? b : 0), 0);
  return (
    <div id={id} role="group" aria-labelledby={`${id}-label`}>
      <div className="grid grid-cols-4 gap-2 sm:grid-cols-5">
        {value.map((n, i) => (
          <label
            key={i}
            className={`rounded-[10px] border bg-paper px-2.5 py-1.5 transition focus-within:border-ink focus-within:shadow-[0_0_0_3px_var(--color-coin-tint)] ${
              Number.isFinite(n) ? 'border-hairline' : 'border-berry-deep'
            }`}
          >
            <span className="block text-[11px] font-semibold text-stone">Day {i + 1}</span>
            <input
              inputMode="numeric"
              className="w-full bg-transparent text-[15px] font-bold tabular-nums outline-none focus-visible:outline-none"
              value={Number.isFinite(n) ? n : ''}
              onChange={(e) => {
                const t = e.target.value.replace(/\D/g, '');
                const next = [...value];
                next[i] = t === '' ? NaN : Number(t);
                onChange(next);
              }}
            />
          </label>
        ))}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <button
          type="button"
          className="btn-ghost h-8 gap-1 px-2.5 py-0 text-[12px] max-sm:h-10"
          disabled={value.length >= 31}
          onClick={() => onChange([...value, value[value.length - 1] ?? 10])}
        >
          <Plus size={13} aria-hidden="true" /> Add a day
        </button>
        <button
          type="button"
          className="btn-ghost h-8 gap-1 px-2.5 py-0 text-[12px] max-sm:h-10"
          disabled={value.length <= 1}
          onClick={() => onChange(value.slice(0, -1))}
        >
          <Minus size={13} aria-hidden="true" /> Remove day {value.length}
        </button>
      </div>
      <p className="mt-2 text-[12px] text-stone">
        A full {value.length}-day streak pays{' '}
        <b className="font-semibold tabular-nums text-coin-ink">{total.toLocaleString()} coins</b> (
        {fmtUsd(total / COINS_PER_USD)}).
      </p>
    </div>
  );
}
